import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { HiUser } from 'react-icons/hi'
import { useCart } from '../context/CartContext'
import { useAuth } from '../context/AuthContext'
import { placeOrder } from '../api/cart'
import Button from '../components/ui/Button'
import SectionTitle from '../components/ui/SectionTitle'

const COUNTRIES = [
  { code: 'EG', name: 'Egypt' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'KW', name: 'Kuwait' },
]

// Friendly labels for common WooCommerce gateway IDs. Anything not listed
// here falls back to a capitalized version of its raw ID, so a newly
// enabled gateway still renders sensibly without a code change.
const PAYMENT_LABELS = {
  cod:           { title: 'Cash on Delivery',        hint: 'Pay when you receive' },
  bacs:          { title: 'Direct Bank Transfer',    hint: 'Pay via bank transfer' },
  cheque:        { title: 'Check Payment',           hint: '' },
  paypal:        { title: 'PayPal',                  hint: 'Redirects to PayPal' },
  'ppcp-gateway':{ title: 'PayPal',                  hint: 'Redirects to PayPal' },
  stripe:        { title: 'Credit / Debit Card',     hint: 'Powered by Stripe' },
  paymob:        { title: 'Card / Mobile Wallet',    hint: 'Powered by Paymob' },
  fawry:         { title: 'Fawry',                   hint: 'Pay at any Fawry outlet' },
}

function paymentLabel(id) {
  return PAYMENT_LABELS[id] ?? { title: id.charAt(0).toUpperCase() + id.slice(1), hint: '' }
}

function Field({ label, type = 'text', value, onChange, placeholder, required, children }) {
  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-light-400 font-body text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors'
  return (
    <div>
      <label className="block font-body text-sm font-semibold text-ink/70 mb-1.5">{label}</label>
      {children ?? (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className={inputClass}
        />
      )}
    </div>
  )
}

export default function Checkout() {
  const { cart, formatPrice, currencySymbol, refreshCart } = useCart()
  const { user, isLoggedIn, openAuthModal } = useAuth()

  const [billing, setBilling] = useState({
    first_name: user?.first_name      ?? '',
    last_name:  user?.last_name       ?? '',
    email:      user?.email           ?? '',
    phone:      user?.billing_phone   ?? '',
    address_1:  user?.billing_address ?? '',
    city:       user?.billing_city    ?? '',
    country:    'EG',
    postcode:   '',
    company:    '',
    state:      '',
  })

  // Pre-fill billing when user logs in during checkout
  useEffect(() => {
    if (!user) return
    setBilling((v) => ({
      ...v,
      first_name: user.first_name      || v.first_name,
      last_name:  user.last_name       || v.last_name,
      email:      user.email           || v.email,
      phone:      user.billing_phone   || v.phone,
      address_1:  user.billing_address || v.address_1,
      city:       user.billing_city    || v.city,
    }))
  }, [user])

  const [note,          setNote]          = useState('')
  const [paymentMethod, setPaymentMethod] = useState('')
  const [isSubmitting,  setIsSubmitting]  = useState(false)
  const [error,         setError]         = useState('')
  const [success,       setSuccess]       = useState(null)

  const items           = cart?.items ?? []
  const total            = cart?.totals?.total_price
  const paymentMethods   = cart?.payment_methods ?? []

  // Default to the first gateway WooCommerce actually reports as available
  // once the cart loads (payment_methods is empty until then).
  useEffect(() => {
    if (paymentMethods.length > 0 && !paymentMethods.includes(paymentMethod)) {
      setPaymentMethod(paymentMethods[0])
    }
  }, [paymentMethods, paymentMethod])

  const setField = (key) => (e) => setBilling((v) => ({ ...v, [key]: e.target.value }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      const result = await placeOrder(billing, paymentMethod, note)
      await refreshCart()
      const redirect = result.payment_result?.redirect_url
      if (redirect && !redirect.includes(window.location.hostname)) {
        window.location.href = redirect
      } else {
        setSuccess(result)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Order confirmed ───────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 py-24 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl shadow-2xl p-10 text-center max-w-md w-full"
        >
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="font-display text-3xl text-ink mb-2">Order Confirmed!</h2>
          <p className="font-body text-ink-muted mb-1">
            Order <strong>#{success.order_id}</strong> has been placed.
          </p>
          <p className="font-body text-sm text-ink-muted mb-8">
            We'll contact you at <strong>{billing.email}</strong> to confirm.
          </p>
          <Button to="/" variant="primary" className="w-full">Back to Home</Button>
        </motion.div>
      </div>
    )
  }

  // ── Empty cart guard ──────────────────────────────────────────────────────
  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center py-24 px-4">
        <div className="text-center">
          <p className="text-5xl mb-4">🛒</p>
          <h2 className="font-display text-2xl text-ink mb-4">Your cart is empty</h2>
          <Button to="/shop" variant="primary">Go Shopping</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-28 pb-16 bg-gradient-to-b from-background to-light-100">
      <div className="container-custom">
        <SectionTitle
          eyebrow="Almost There"
          title="Checkout"
          centered={false}
          className="mb-10"
        />

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* ── Left: Billing + Payment ───────────────────────────────── */}
            <div className="lg:col-span-2 space-y-6">

              {/* Guest auth banner */}
              {!isLoggedIn && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-primary/5 border border-primary/15"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <HiUser size={18} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-body text-sm font-semibold text-ink">Returning customer?</p>
                      <p className="font-body text-xs text-ink-muted">Sign in to pre-fill your details and track orders.</p>
                    </div>
                  </div>
                  <div className="flex gap-3 shrink-0">
                    <button
                      type="button"
                      onClick={() => openAuthModal('login')}
                      className="px-4 py-2 rounded-full border border-primary text-primary font-body text-sm font-semibold hover:bg-primary hover:text-white transition-colors duration-200"
                    >
                      Sign In
                    </button>
                    <button
                      type="button"
                      onClick={() => openAuthModal('register')}
                      className="px-4 py-2 rounded-full bg-primary text-white font-body text-sm font-semibold hover:bg-primary-dark transition-colors duration-200"
                    >
                      Register
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Billing details */}
              <div className="bg-white rounded-2xl p-6 shadow-sm space-y-5">
                <h3 className="font-display text-lg text-ink">Billing Details</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="First Name *" value={billing.first_name} onChange={setField('first_name')} required />
                  <Field label="Last Name *"  value={billing.last_name}  onChange={setField('last_name')}  required />
                </div>
                <Field label="Email *"   type="email" value={billing.email} onChange={setField('email')} required />
                <Field label="Phone *"   type="tel"   value={billing.phone} onChange={setField('phone')} placeholder="+20 100 000 0000" required />
                <Field label="Address *" value={billing.address_1} onChange={setField('address_1')} placeholder="Street address" required />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="City *" value={billing.city} onChange={setField('city')} required />
                  <Field label="Country">
                    <select
                      value={billing.country}
                      onChange={setField('country')}
                      className="w-full px-4 py-3 rounded-xl border border-light-400 font-body text-sm focus:outline-none focus:border-primary transition-colors"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </Field>
                </div>

                <Field label="Order Notes (optional)">
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Any special instructions for your order?"
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl border border-light-400 font-body text-sm focus:outline-none focus:border-primary transition-colors resize-none"
                  />
                </Field>
              </div>

              {/* Payment method */}
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-display text-lg text-ink mb-4">Payment Method</h3>
                <div className="space-y-3">
                  {paymentMethods.length === 0 && (
                    <p className="font-body text-sm text-ink-muted">Loading payment options…</p>
                  )}
                  {paymentMethods.map((id) => {
                    const { title, hint } = paymentLabel(id)
                    return (
                      <label
                        key={id}
                        className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border border-light-200 hover:border-primary/40 transition-colors"
                      >
                        <input
                          type="radio"
                          name="payment"
                          value={id}
                          checked={paymentMethod === id}
                          onChange={() => setPaymentMethod(id)}
                          className="accent-primary w-4 h-4"
                        />
                        <span className="font-body text-sm font-semibold text-ink">{title}</span>
                        {hint && <span className="ml-auto font-body text-xs text-ink-muted">{hint}</span>}
                      </label>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* ── Right: Order summary ──────────────────────────────────── */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-28">
                <h3 className="font-display text-lg text-ink mb-4">Order Summary</h3>

                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-1">
                  {items.map((item) => (
                    <div key={item.key} className="flex justify-between gap-3">
                      <span className="font-body text-sm text-ink-muted leading-snug line-clamp-2 flex-1">
                        {item.name} × {item.quantity}
                      </span>
                      <span className="font-body text-sm text-ink shrink-0">
                        {currencySymbol} {formatPrice(item.totals.line_total)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="border-t border-light-200 pt-4 flex justify-between items-center">
                  <span className="font-body font-semibold text-ink">Total</span>
                  <span className="font-display text-xl text-primary">
                    {currencySymbol} {formatPrice(total)}
                  </span>
                </div>

                {error && (
                  <div className="mt-4 p-3 rounded-xl bg-red-50 border border-red-100">
                    <p className="font-body text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full mt-5"
                  loading={isSubmitting}
                  disabled={isSubmitting || !paymentMethod}
                >
                  Place Order
                </Button>

                <p className="font-body text-xs text-ink-muted text-center mt-3">
                  By placing your order you agree to our terms and conditions.
                </p>
              </div>
            </div>

          </div>
        </form>
      </div>
    </div>
  )
}
